import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiGet } from "@/lib/api";
import type { PagedResponse, PostDto } from "@/types";
import { PostCard } from "@/components/content/PostCard";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { CategoryTabs } from "@/components/widgets/CategoryTabs";

/**
 * "Aktuelles" index with the three fixed category tabs
 * (Informationen / Veranstaltungen / Ereignisse). The category filter
 * is applied client-side so we keep using the existing paged endpoint
 * without backend changes; the spec only requires the *visual* split.
 */
export function PostsIndexPage() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") ?? "0");
  const cat = params.get("cat") ?? "alle";

  const { data } = useQuery({
    queryKey: ["posts", "de", page],
    queryFn: () =>
      apiGet<PagedResponse<PostDto>>(
        `/public/posts?locale=${"de"}&page=${page}&size=18`,
      ),
  });

  const filtered = (data?.content ?? []).filter((p) =>
    cat === "alle" ? true : p.category?.toLowerCase() === cat,
  );

  return (
    <>
      <Helmet>
        <title>{"Aktuelles"} | Falkenrehde</title>
      </Helmet>
      <Breadcrumb items={[{ label: "Aktuelles" }]} />
      <h1 className="font-serif text-3xl lg:text-4xl font-bold mb-6 text-primary">
        {"Aktuelles"}
      </h1>

      <CategoryTabs />

      {data && filtered.length === 0 && (
        <p className="text-text-muted italic">{"Keine Einträge gefunden."}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filtered.map((p) => (
          <PostCard key={p.id} post={p} />
        ))}
      </div>

      {data && data.totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={() => {
              params.set("page", String(Math.max(0, page - 1)));
              setParams(params);
            }}
            disabled={page === 0}
            className="px-4 py-2 border border-border rounded-md disabled:opacity-50"
          >
            &larr;
          </button>
          <span className="text-sm text-text-muted">
            {"Seite " + (page + 1) + " von " + data.totalPages}
          </span>
          <button
            onClick={() => {
              params.set("page", String(Math.min(data.totalPages - 1, page + 1)));
              setParams(params);
            }}
            disabled={page >= data.totalPages - 1}
            className="px-4 py-2 border border-border rounded-md disabled:opacity-50"
          >
            &rarr;
          </button>
        </div>
      )}
    </>
  );
}
